import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Alert } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import FloatingActionButton from './FloatingActionButton'
import { useContent } from '../hooks/useContent'
import Footer from './footer'
import heroBg from '../assets/imgDisp/cHero.jpg'
import './Contact.css'

const Contact = () => {
  const buildGmailComposeUrl = (mailtoLink, subject = '', body = '') => {
    try {
      const raw = (mailtoLink || '').replace('mailto:', '')
      const email = encodeURIComponent(raw)
      const su = subject ? `&su=${encodeURIComponent(subject)}` : ''
      const bd = body ? `&body=${encodeURIComponent(body)}` : ''
      return `https://mail.google.com/mail/?view=cm&fs=1&to=${email}${su}${bd}`
    } catch (_) {
      return mailtoLink || ''
    }
  }
  const isMobileDevice = () => {
    try {
      const ua = navigator.userAgent || ''
      return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua)
    } catch (_) {
      return false
    }
  }
  const openEmailSmart = (mailtoLink, subject = '', body = '') => {
    if (!mailtoLink || !mailtoLink.startsWith('mailto:')) return
    const raw = mailtoLink.replace('mailto:', '')
    const to = raw.split('?')[0] || ''
    const su = subject || 'Message from Ampthilldowns'
    const bd = body || ''

    const gmailScheme = `googlegmail://co?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(su)}&body=${encodeURIComponent(bd)}`
    const mailtoHref = `mailto:${to}?subject=${encodeURIComponent(su)}&body=${encodeURIComponent(bd)}`
    const webCompose = buildGmailComposeUrl(`mailto:${to}`, su, bd)
    const accountChooser = `https://accounts.google.com/AccountChooser?service=mail&continue=${encodeURIComponent(webCompose)}`

    if (isMobileDevice()) {
      // Try opening Gmail app first; fallback to default mail client
      const tryApp = () => { window.location.href = gmailScheme }
      const tryMailto = () => { window.location.href = mailtoHref }
      tryApp()
      setTimeout(() => {
        // If app not opened, fallback to mailto
        tryMailto()
      }, 800)
      return
    }

    // Desktop: open Gmail in a new tab via account chooser
    try {
      window.open(accountChooser, '_blank', 'noopener,noreferrer')
    } catch (_) {
      window.open(webCompose, '_blank')
    }
  }
  const navigate = useNavigate()
  const { content, loading, error } = useContent('contact')


  // Use content with fallbacks
  const heroContent = content?.hero || {
    title: 'Get in Touch',
    subtitle: "We're here to help you plan your perfect nature retreat",
    backgroundImage: '',
  }
  const contactInfoContent = content?.contactInfo || []
  const contactInfoSection = content?.contactInfoSection || {
    title: 'Contact Information',
    subtitle: 'Reach out to us through any of these channels',
  }
  const mapContent = content?.mapSection || {
    title: 'Find Us',
    mapEmbed: '',
    locationDetails: [],
    directions: [],
  }
  const quickActionsContent = content?.quickActions || {
    title: 'Quick Actions',
    subtitle: '',
    actions: [],
  }

  // Start with local fallback visible immediately
  const [heroImage, setHeroImage] = useState(heroBg)
  const [heroLoaded, setHeroLoaded] = useState(true)


  useEffect(() => {

    if (loading) return
    let cancelled = false
    const desiredHero = (heroContent?.backgroundImage || '').trim()

    const load = (src) => {
      const img = new Image()
      img.onload = () => {
        if (cancelled) return
        setHeroImage(src)
        setHeroLoaded(true)
      }
      img.onerror = () => {
        if (cancelled) return
        if (src !== heroBg) {
          load(heroBg)
        } else {
          setHeroImage(heroBg)
          setHeroLoaded(true)
        }
      }
      img.src = src
    }

    if (desiredHero) {
      load(desiredHero)
    } else {
      setHeroImage(heroBg)
      setHeroLoaded(true)
    }

    return () => {
      cancelled = true
    }
  }, [loading, heroContent?.backgroundImage])

  



  // Removed loading spinner: render page immediately with fallback content.

  // Show error state
  if (error) {
    return (
      <div className='contact-page'>
        <Alert variant='danger' className='text-center mb-0'>
          <strong>Content Loading Error:</strong> {error}
          <p className='mt-2'>Please try refreshing the page.</p>
        </Alert>
      </div>
    )
  }

  // Unified Gmail-first email flow (mirrors RoomDetail) for any mailto links
  // Removed legacy openEmail to avoid unused variable lint warnings

  return (
    <div className='contact-page'>
      {/* Hero Section */}
      <section className='contact-hero-section'>
        {heroImage && (
          <img
            src={heroImage}
            alt="Hero"
            className='hero-image-tag'
            loading='eager'
            fetchPriority='high'
            decoding='async'
            style={{ display: 'block', width: '100%', height: 'auto' }}
          />
        )}
        <div className={`hero-overlay ${heroLoaded ? 'is-loaded' : ''}`}>
          <div className='hero-content container text-center'>
            <h1 className='hero-title'>{heroContent.title}</h1>
            <p className='hero-subtitle'>{heroContent.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className='contact-info-section py-5'>
        <Container>
          <div className='text-center mb-5'>
            <h2 className='section-head'>{contactInfoSection.title}</h2>
            <p className='section-subtitle'>{contactInfoSection.subtitle}</p>
          </div>

          <Row className='g-4'>
            {contactInfoContent.map((info, index) => (
              <Col md={6} lg={3} key={index}>
                <div className='contact-card'>
                  <div className='contact-icon'>{info.icon}</div>
                  <h3 className='contact-title'>{info.title}</h3>
                  <div className='contact-details'>
                    {info.details.map((detail, i) => (
                      <p key={i} className='contact-detail'>
                        {detail}
                      </p>
                    ))}
                  </div>
                  {info.actionLink && !info.actionLink.startsWith('tel:') && (
                    info.actionLink.startsWith('mailto:') ? (
                      <button
                        type='button'
                        className='contact-action btn-reset'
                        onClick={() => openEmailSmart(info.actionLink, info.subject, info.body)}
                        aria-label={`Email ${info.title}`}
                      >
                        {info.action || 'Email'}
                      </button>
                    ) : (
                      <a
                        href={info.actionLink}
                        className='contact-action'
                        target={info.actionLink.startsWith('http') ? '_blank' : '_self'}
                        rel={info.actionLink.startsWith('http') ? 'noopener noreferrer' : ''}
                      >
                        {info.action}
                      </a>
                    )
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Map & Location Info */}
      <section className='contact-form-section py-5 bg-light'>
        <Container>
          <Row className='g-5 justify-content-center'>
            {/* Map & Additional Info */}
            <Col lg={8}>
              <div className='map-wrapper'>
                <h3 className='map-title'>{mapContent.title}</h3>
                <div className='map-container'>
                  <iframe
                    src={(() => {
                      const mapUrl = mapContent.mapEmbed
                      // Validate URL: must be a valid Google Maps embed URL
                      if (
                        mapUrl &&
                        typeof mapUrl === 'string' &&
                        (mapUrl.includes('google.com/maps/embed') ||
                          mapUrl.startsWith('https://maps.google.com'))
                      ) {
                        return mapUrl
                      }
                      // Fallback to correct Google Maps embed
                      return 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5097.741159095042!2d77.3354157760227!3d10.193179369678859!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b077a81850b1f51%3A0xdcb5c6e9897cb2e9!2sAmpthill%20Downs%20-%20The%20Farm%20Stay!5e1!3m2!1sen!2sin!4v1763549083611!5m2!1sen!2sin'
                    })()}
                    width='100%'
                    height='300'
                    style={{ border: 0, borderRadius: '12px' }}
                    allowFullScreen=''
                    loading='lazy'
                    referrerPolicy='no-referrer-when-downgrade'
                    title='Ampthill Eco-Resort Location'
                  ></iframe>
                </div>

                <div className='location-details mt-4'>
                  {mapContent.locationDetails.map((detail, index) => (
                    <div key={index} className='location-item'>
                      <strong>{detail.label}:</strong> {detail.value}
                    </div>
                  ))}
                </div>

                <div className='directions-section mt-4'>
                  <h4>How to Reach Us</h4>
                  {mapContent.directions.map((direction, index) => (
                    <div key={index} className='direction-method'>
                      <strong>{direction.method}:</strong>
                      <p>{direction.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Quick Actions */}
      <section className='quick-actions-section py-5'>
        <Container>
          <div className='text-center mb-5'>
            <h2 className='section-head'>{quickActionsContent.title}</h2>
            <p className='section-subtitle'>{quickActionsContent.subtitle}</p>
          </div>

          <Row className='g-4 justify-content-center'>
            {quickActionsContent.actions.map((action, index) => (
              <Col md={4} key={index}>
                <div className='quick-action-card'>
                  <div className='action-icon'>{action.icon}</div>
                  <h3>{action.title}</h3>
                  <p>{action.description}</p>
                  <button
                    onClick={() => {
                      if (action.title === 'Book Now') {
                        navigate('/stays')
                      } else if (action.title === 'Plan Journey') {
                        navigate('/plan-your-journey')
                      }
                    }}
                    className='action-btn'
                  >
                    {action.buttonText}
                  </button>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <Footer />

      <FloatingActionButton />
    </div>
  )
}

export default Contact
